import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type ButtonProps = useRender.ComponentProps<"button"> & VariantProps<typeof buttonVariants>;

/**
 * Polymorphic via Base UI's `render` prop, not Radix's `asChild`/`Slot`:
 *   <Button render={<Link to="/companies/new">Add Company</Link>} />
 * instead of `<Button asChild><Link ...>Add Company</Link></Button>`. The
 * element passed to `render` carries its own children — Button itself
 * takes none in that form.
 */
export function Button({
  className,
  variant,
  size,
  render = <button />,
  ref,
  ...props
}: ButtonProps) {
  return useRender({
    render,
    ref,
    props: {
      ...props,
      "data-slot": "button",
      className: cn(buttonVariants({ variant, size }), className),
    },
  });
}

export { buttonVariants };
